import { Plural, useLingui } from "@lingui/react/macro";
import { useFormContext, type FieldValues } from "react-hook-form";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import Select from "@medoma/components/Select/Select";
import InputField from "@medoma/components/InputField/InputField";
import Fieldset from "@medoma/components/Fieldset/Fieldset";
import {
  useFieldPathWithPrefix,
  useFieldErrorWithPrefix,
} from "@/forms/FormFieldPathPrefixContext";
import {
  batchTemplateActivityDelayedStartDateOptionsSchema,
  batchTemplateActivityDelayedStartDateUnitSchema,
  type BatchTemplateActivityFormFields,
  type IBatchTemplateActivityDelayedStartDateOption,
} from "@/pages/template-manager/BatchTemplateForm/batchTemplateFormSchema";
import { weekdaySchema } from "@/api/Activities";
import { weekdayDictionary } from "@/forms/AddActivityForm/Scheduling/schedulingModels";
import styles from "./BatchTemplateActivityStartDateDelayPicker.module.scss";

export const BatchTemplateActivityStartDateDelayPicker = ({
  onDelayedStartDateChange,
}: {
  onDelayedStartDateChange?: (
    newValue: IBatchTemplateActivityDelayedStartDateOption,
  ) => void;
}) => {
  const { t } = useLingui();
  const {
    formState: { errors },
    register,
    watch,
  } = useFormContext<FieldValues>();

  const delayedStartDateFieldName =
    useFieldPathWithPrefix<BatchTemplateActivityFormFields>("delayedStartDate");
  const delayedStartDateOffsetFieldName =
    useFieldPathWithPrefix<BatchTemplateActivityFormFields>(
      "delayedStartDateOffset",
    );
  const delayedStartDateUnitFieldName =
    useFieldPathWithPrefix<BatchTemplateActivityFormFields>(
      "delayedStartDateUnit",
    );
  const delayedStartDateWeekdayFieldName =
    useFieldPathWithPrefix<BatchTemplateActivityFormFields>(
      "delayedStartDateWeekday",
    );

  const errorMessageDelayedStartDate =
    useFieldErrorWithPrefix<BatchTemplateActivityFormFields>(
      errors,
      "delayedStartDate",
    );
  const errorMessageDelayedStartDateOffset =
    useFieldErrorWithPrefix<BatchTemplateActivityFormFields>(
      errors,
      "delayedStartDateOffset",
    );
  const errorMessageDelayedStartDateWeekday =
    useFieldErrorWithPrefix<BatchTemplateActivityFormFields>(
      errors,
      "delayedStartDateWeekday",
    );

  const delayedStartDate = watch(delayedStartDateFieldName);
  const delayedStartDateOffset = watch(delayedStartDateOffsetFieldName);

  const handleDelayedStartDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (!onDelayedStartDateChange) {
      return;
    }

    const parsedNewValue =
      batchTemplateActivityDelayedStartDateOptionsSchema.parse(e.target.value);
    onDelayedStartDateChange(parsedNewValue);
  };

  const delayedStartDateField = register(delayedStartDateFieldName, {
    required: {
      value: true,
      message: t`Välj inställningar för startdatum`,
    },
    onChange: handleDelayedStartDateChange,
  });

  return (
    <>
      <RadioButtons
        legend={t`Startdatum`}
        description={t`Antingen används det startdatum som sätts när mallen används, eller så sätts startdatum automatiskt med en förskjutning enligt nedan.`}
        errorMessage={errorMessageDelayedStartDate}
      >
        <RadioButton
          label={{ text: t`Ingen förskjutning` }}
          value={batchTemplateActivityDelayedStartDateOptionsSchema.enum.none}
          {...delayedStartDateField}
        />
        <RadioButton
          label={{
            text: t`Förskjutet (välj antal dagar eller veckor)`,
          }}
          value={batchTemplateActivityDelayedStartDateOptionsSchema.enum.offset}
          {...delayedStartDateField}
        />
        <RadioButton
          label={{
            text: t`På nästkommande veckodag (välj veckodag)`,
          }}
          value={
            batchTemplateActivityDelayedStartDateOptionsSchema.enum.weekday
          }
          {...delayedStartDateField}
        />
      </RadioButtons>

      {delayedStartDate ===
      batchTemplateActivityDelayedStartDateOptionsSchema.enum.offset ? (
        <Fieldset
          legend={t`Förskjutning`}
          errorMessage={errorMessageDelayedStartDateOffset}
        >
          <div className={styles.delayedStartDateFields}>
            <InputField
              label={t`Förskjutning`}
              hideLabel
              showOptionalLabel={false}
              type="text"
              outerWidth="fit"
              {...register(delayedStartDateOffsetFieldName, {
                required: {
                  value: true,
                  message: t`Ange antal dagar eller veckor för förskjutet startdatum`,
                },
                pattern: {
                  value: /^[0-9]+$/,
                  message: t`Ange endast siffror`,
                },
                validate: (value) => {
                  const number = parseInt(value, 10);
                  if (number < 1) {
                    return t`Ange ett värde som är större än 0`;
                  }
                  return true;
                },
              })}
            />
            <Select
              label={t`Enhet för förskjutning`}
              hideLabel
              {...register(delayedStartDateUnitFieldName)}
            >
              <option
                value={batchTemplateActivityDelayedStartDateUnitSchema.enum.day}
              >
                <Plural
                  value={delayedStartDateOffset ? delayedStartDateOffset : "0"}
                  _1="dag"
                  other="dagar"
                />
              </option>
              <option
                value={
                  batchTemplateActivityDelayedStartDateUnitSchema.enum.week
                }
              >
                <Plural
                  value={delayedStartDateOffset ? delayedStartDateOffset : "0"}
                  _1="vecka"
                  other="veckor"
                />
              </option>
            </Select>
          </div>
        </Fieldset>
      ) : null}

      {delayedStartDate ===
      batchTemplateActivityDelayedStartDateOptionsSchema.enum.weekday ? (
        <RadioButtons
          orientation="horizontal"
          legend={t`Förskjutning till nästkommande veckodag`}
          errorMessage={errorMessageDelayedStartDateWeekday}
        >
          {Object.values(weekdaySchema.enum).map((weekday) => (
            <RadioButton
              key={weekday}
              label={{ text: t(weekdayDictionary[weekday].short) }}
              {...register(delayedStartDateWeekdayFieldName, {
                required: {
                  value: true,
                  message: t`Välj veckodag för förskjutet startdatum`,
                },
              })}
              value={weekday}
              visualStyle="framed"
            />
          ))}
        </RadioButtons>
      ) : null}
    </>
  );
};
